/**
 * VEDHACART Cart Mode Switcher
 * Saves and restores cart when switching between Eats and Shop modes
 * 
 * Storage:
 * - Guest & Logged in: localStorage (vc_cart_eats, vc_cart_shop)
 * - Future: Customer metafields for cross-device sync
 */

(function() {
  'use strict';

  // Prevent double initialization
  if (window.__VC_CART_MODE_INIT__) return;
  window.__VC_CART_MODE_INIT__ = true;

  // Configuration
  var CONFIG = {
    STORAGE_KEY_EATS: 'vc_cart_eats',
    STORAGE_KEY_SHOP: 'vc_cart_shop',
    MODE_CHANGE_EVENT: 'veda:mode:changed',
    DEBUG: false
  };

  // Logging
  function log() {
    if (CONFIG.DEBUG) {
      console.log.apply(console, ['[vc-cart-mode]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  // Get storage key for mode
  function getStorageKey(mode) {
    return mode === 'eats' ? CONFIG.STORAGE_KEY_EATS : CONFIG.STORAGE_KEY_SHOP;
  }

  /**
   * Get current Shopify cart
   */
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); });
  }

  /**
   * Clear Shopify cart
   */
  function clearCart() {
    return fetch('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    }).then(function(r) { return r.json(); });
  }

  /**
   * Add items to Shopify cart
   */
  function addItemsToCart(items) {
    if (!items || items.length === 0) {
      return Promise.resolve({ items: [] });
    }

    // Format items for Shopify API
    var cartItems = items.map(function(item) {
      var cartItem = {
        id: parseInt(item.variant_id || item.id, 10),
        quantity: parseInt(item.quantity, 10) || 1
      };
      
      // Include properties if present
      if (item.properties && Object.keys(item.properties).length > 0) {
        cartItem.properties = item.properties;
      }
      
      return cartItem;
    });

    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    })
    .then(function(r) { return r.json(); })
    .catch(function(err) {
      console.error('[vc-cart-mode] Failed to add items:', err);
      return { items: [] };
    });
  }

  /**
   * Extract cart items for saving
   */
  function extractCartItems(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return [];
    }

    return cart.items.map(function(item) {
      var savedItem = {
        variant_id: item.variant_id,
        quantity: item.quantity,
        product_id: item.product_id,
        title: item.product_title,
        variant_title: item.variant_title,
        image: item.image,
        price: item.price,
        url: item.url
      };

      // Include properties if present
      if (item.properties && Object.keys(item.properties).length > 0) {
        savedItem.properties = item.properties;
      }

      return savedItem;
    });
  }

  /**
   * Save cart to localStorage
   */
  function saveCartForMode(mode, items) {
    try {
      var key = getStorageKey(mode);
      localStorage.setItem(key, JSON.stringify(items));
      log('Saved to localStorage:', key, items.length, 'items');
      return Promise.resolve(true);
    } catch (e) {
      console.error('[vc-cart-mode] localStorage save failed:', e);
      return Promise.resolve(false);
    }
  }

  /**
   * Load cart from localStorage
   */
  function loadCartForMode(mode) {
    try {
      var key = getStorageKey(mode);
      var data = localStorage.getItem(key);
      if (data) {
        var items = JSON.parse(data);
        log('Loaded from localStorage:', key, items.length, 'items');
        return Promise.resolve(items);
      }
    } catch (e) {
      console.error('[vc-cart-mode] localStorage load failed:', e);
    }
    return Promise.resolve([]);
  }

  /**
   * Clear saved cart for mode
   */
  function clearSavedCart(mode) {
    try {
      var key = getStorageKey(mode);
      localStorage.removeItem(key);
      log('Cleared saved cart:', key);
    } catch (e) {
      console.error('[vc-cart-mode] Clear failed:', e);
    }
  }

  /**
   * Get saved item count for a mode (for badge display)
   */
  function getSavedItemCount(mode) {
    try {
      var key = getStorageKey(mode);
      var data = localStorage.getItem(key);
      if (data) {
        var items = JSON.parse(data);
        return items.reduce(function(total, item) {
          return total + (parseInt(item.quantity, 10) || 1);
        }, 0);
      }
    } catch (e) {}
    return 0;
  }

  /**
   * Get saved items array for a mode
   */
  function getSavedItems(mode) {
    try {
      var key = getStorageKey(mode);
      var data = localStorage.getItem(key);
      if (data) {
        return JSON.parse(data);
      }
    } catch (e) {}
    return [];
  }

  /**
   * Update all cart count elements in the UI (header, mobile tab bar, etc.)
   */
  function refreshCartUI(cart) {
    var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;

    // Update all cart count elements
    var selectors = [
      '[data-cart-count]',
      '#cart-count',
      '.cart-count',
      '.cart-badge',
      '#mCartCount',           // Mobile tab bar
      '#cartbar-count'         // Eats mobile cart bar
    ];

    selectors.forEach(function(sel) {
      document.querySelectorAll(sel).forEach(function(el) {
        el.textContent = String(count);
        
        // Handle hidden attribute
        if (el.hasAttribute('hidden') || el.getAttribute('hidden') === '') {
          if (count > 0) {
            el.removeAttribute('hidden');
          }
        } else if (count === 0) {
          el.setAttribute('hidden', '');
        }
      });
    });

    // Update Eats cartbar total if present (use items_subtotal_price to exclude checkout discounts)
    var cartbarTotal = document.getElementById('cartbar-total');
    if (cartbarTotal && cart) {
      var total = (cart.items_subtotal_price / 100).toFixed(2);
      cartbarTotal.textContent = total;
    }

    log('Updated cart UI - count:', count);
  }

  /**
   * Show notification toast
   */
  function showNotification(message, type) {
    type = type || 'info';
    
    // Use existing toast if available
    var toast = document.getElementById('toast');
    var textEl = document.getElementById('toast__text');
    
    if (toast && textEl) {
      textEl.textContent = message;
      toast.hidden = false;
      
      // Style based on type
      if (type === 'success') {
        toast.style.background = '#2D6A4F';
      } else if (type === 'warning') {
        toast.style.background = '#B45309';
      } else {
        toast.style.background = '#111';
      }
      
      setTimeout(function() {
        toast.hidden = true;
        toast.style.background = '#111';
      }, 4000);
    }

    log('Notification:', message, type);
  }

  /**
   * Update UI badges showing saved items in other mode
   * Badge only appears on the NON-ACTIVE mode button
   */
  function updateOtherModeBadge() {
    var currentMode = window.ModeManager ? window.ModeManager.getCurrentMode() : 'shop';
    var otherMode = currentMode === 'eats' ? 'shop' : 'eats';
    var otherModeCount = getSavedItemCount(otherMode);

    // Update any generic badge elements
    var badges = document.querySelectorAll('[data-other-mode-count]');
    badges.forEach(function(badge) {
      badge.textContent = otherModeCount;
      badge.hidden = otherModeCount === 0;
    });

    // REMOVE badge from CURRENT mode button (should not show badge on active mode)
    var currentModeBtn = document.querySelector('.mode-seg__btn[data-mode="' + currentMode + '"]');
    if (currentModeBtn) {
      var currentBadge = currentModeBtn.querySelector('.mode-seg__saved-badge');
      if (currentBadge) {
        currentBadge.remove();
      }
    }

    // ADD/UPDATE badge on OTHER mode button only
    // Shop badge → left side, Eats badge → right side
    var otherModeBtn = document.querySelector('.mode-seg__btn[data-mode="' + otherMode + '"]');
    if (otherModeBtn) {
      var existingBadge = otherModeBtn.querySelector('.mode-seg__saved-badge');
      
      if (otherModeCount > 0) {
        if (!existingBadge) {
          existingBadge = document.createElement('span');
          existingBadge.className = 'mode-seg__saved-badge';
          
          // Position: Shop badge on left, Eats badge on right
          var positionStyle = otherMode === 'shop' 
            ? 'left:-6px;right:auto;' 
            : 'right:-6px;left:auto;';
          
          existingBadge.style.cssText = 'position:absolute;top:-6px;' + positionStyle + 'background:#E85D04;color:#fff;font-size:10px;font-weight:700;padding:2px 6px;border-radius:10px;min-width:16px;text-align:center;line-height:1.2;box-shadow:0 2px 4px rgba(0,0,0,0.2);';
          otherModeBtn.style.position = 'relative';
          otherModeBtn.appendChild(existingBadge);
        }
        existingBadge.textContent = otherModeCount;
      } else if (existingBadge) {
        existingBadge.remove();
      }
    }

    log('Updated badge - current:', currentMode, '| other:', otherMode, '(' + otherModeCount + ' items)');
  }

  /**
   * Main function: Handle mode switch
   */
  function handleModeSwitch(newMode, previousMode) {
    // Only handle eats <-> shop switches
    if ((previousMode !== 'eats' && previousMode !== 'shop') || 
        (newMode !== 'eats' && newMode !== 'shop')) {
      return Promise.resolve();
    }

    // Same mode, no action needed
    if (newMode === previousMode) {
      return Promise.resolve();
    }

    log('Mode switch:', previousMode, '->', newMode);

    // Step 1: Get current cart
    return getCart()
      .then(function(currentCart) {
        var currentItems = extractCartItems(currentCart);
        var currentCount = currentCart.item_count || 0;

        // Step 2: Save current cart for previous mode (only if has items)
        var savePromise = Promise.resolve();
        if (currentCount > 0) {
          savePromise = saveCartForMode(previousMode, currentItems);
          log('Saving', currentCount, 'items for', previousMode);
        }

        return savePromise.then(function() {
          return { savedCount: currentCount, savedMode: previousMode };
        });
      })
      .then(function(result) {
        // Step 3: Clear current cart
        return clearCart().then(function() {
          return result;
        });
      })
      .then(function(result) {
        // Step 4: Load saved cart for new mode
        return loadCartForMode(newMode).then(function(savedItems) {
          return {
            savedCount: result.savedCount,
            savedMode: result.savedMode,
            loadItems: savedItems
          };
        });
      })
      .then(function(result) {
        // Step 5: Add loaded items to cart
        if (result.loadItems && result.loadItems.length > 0) {
          return addItemsToCart(result.loadItems).then(function(addResult) {
            // Clear saved cart after successfully loading
            clearSavedCart(newMode);
            return result;
          });
        }
        return result;
      })
      .then(function(result) {
        // Step 6: Update UI (no notification - silent switch)
        updateOtherModeBadge();

        // Dispatch cart updated event and refresh UI
        return getCart().then(function(cart) {
          refreshCartUI(cart);
          document.dispatchEvent(new CustomEvent('vc:cart:updated', { detail: cart }));
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
          return cart;
        });
      })
      .catch(function(err) {
        console.error('[vc-cart-mode] Mode switch error:', err);
        showNotification('Error switching cart mode', 'warning');
      });
  }

  /**
   * Initialize
   */
  function init() {
    log('Initializing...');

    // Listen for mode changes
    document.addEventListener(CONFIG.MODE_CHANGE_EVENT, function(e) {
      var detail = e.detail || {};
      
      // Only process user-initiated mode changes (clicks)
      if (detail.source === 'click') {
        handleModeSwitch(detail.mode, detail.previousMode);
      }
    });

    // Initial badge update
    setTimeout(updateOtherModeBadge, 100);

    log('Initialized');
  }

  // Expose API
  window.VCCartMode = {
    handleModeSwitch: handleModeSwitch,
    saveCartForMode: saveCartForMode,
    loadCartForMode: loadCartForMode,
    getSavedItemCount: getSavedItemCount,
    getSavedItems: getSavedItems,
    clearSavedCart: clearSavedCart,
    updateOtherModeBadge: updateOtherModeBadge
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
